/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-04-13 09:59:25
 */
import React, { useEffect, useRef } from 'react'
import { Button, Modal } from 'antd'
import {
  useAppDispatch,
  useAppSelector,
  usePathValue,
  usePayment
} from '../../../../app/hooks'
import sensors, { sensorsRole } from '../../../../utils/sensors'
import crown_btn_icon from '../assets/base_vip_icon.png'
import vip_icon from '../assets/vip_icon.png'
import { setDownloadSettingModalVisible } from '../../../../actions/main'
import priceIcon1 from '../assets/price_0.1.png'
import CONFIG from '../../../../constants/config'
import {
  isAdvancedSelection,
  generateFolderNamingParams,
  sensorsNameRule,
  getAdvancedNamingLabels,
  filterBasicNamingRule
} from '../utils'
import { useTrial } from '../hooks/useTrial'
import { ExclamationCircleFilled, SettingOutlined } from '@ant-design/icons'

const Footer = (props) => {
  const {
    namingRule,
    onOk,
    onDownloadOnce,
    disableBatchDownload,
    folderLevels,
    photoTitleList,
    track,
    myRole,
    onChangeSelectNameData
  } = props
  const abTestConfig = useAppSelector((state) => state.main.abTestConfig)
  const chargePage = !!abTestConfig?.groupWebVipAB?.chargePage

  // 判断是否应该显示高级VIP图标
  const shouldShowVipIcon = () => {
    // 检查是否有有效的多级分类下载（不等于undefined和空字符串，长度大于1）
    const hasMultiLevelClassification =
      folderLevels &&
      folderLevels.filter(
        (level) => level.field !== undefined && level.field !== ''
      ).length > 1

    // 检查是否有高级命名规则
    const hasAdvancedNamingRule =
      namingRule &&
      namingRule.some(
        (rule) => rule !== 'user' && rule !== 'time' && rule !== 'watermark'
      )

    // 检查是否有地点选项
    const hasAddressOption =
      folderLevels && folderLevels.some((level) => level.field === 'address')

    return (
      // hasMultiLevelClassification || hasAdvancedNamingRule || hasAddressOption
      hasMultiLevelClassification || hasAddressOption
    )
  }
  const dispatch = useAppDispatch()
  const isDesktop = useAppSelector((state) => state.main.isDesktop)
  const groupID = useAppSelector((state) => state.group.groupId)
  const isVip = useAppSelector((state) => state.group.isVip)
  const limitInfo = useAppSelector((state) => state.groupLimit.limitInfo)
  const pathValue = usePathValue()
  const isVipRef = useRef(isVip)
  const payment = usePayment()
  const vipKind = useAppSelector((state) => state.group.vipInfo?.vip_kind)
  const isSVip = vipKind === CONFIG.VIP_KIND.SVIP
  const isVVip = vipKind === CONFIG.VIP_KIND.VVIP
  const isBasicMember = !!isVip && !isSVip && !isVVip

  const onPay = () => {
    sensors.track('web_click_button_workgroup_go_vip_trigger', {
      place: pathValue,
      functionType: 'photoDownload',
      groupID
    })

    payment.open('allPhotoDownloadPhoto')
  }

  const handleOpenDownloadSetting = () => {
    dispatch(setDownloadSettingModalVisible(true))
  }

  const { multiLeft, renameLeft, refresh } = useTrial({ autoFetch: false })

  // 生成每一级文件夹的命名规则参数
  const generateFolderNamingParamsForFooter = () => {
    return generateFolderNamingParams(folderLevels, photoTitleList)
  }

  // 试用次数用完弹窗埋点上报；namingRuleForMethod 可选，用于「去掉高级命名」等场景与实际上传命名一致
  const trackTrialLimitModal = (
    clickItem: string,
    noticeType: string,
    namingRuleForMethod?: string[]
  ) => {
    const folderParams = generateFolderNamingParamsForFooter()
    const ruleForNamingMethod = namingRuleForMethod ?? namingRule
    sensors.track('workgroup_b4347_c7834_click', {
      clickItem,
      string1: folderParams.string1,
      string2: folderParams.string2,
      string3: folderParams.string3,
      namingMethod: sensorsNameRule(ruleForNamingMethod, photoTitleList),
      noticeType,
      role: sensorsRole(myRole),
      groupID: groupID,
      requirement_id: 721,
      track_sign: 'aworkgroup.b4347.c7834.click.workgroup_b4347_c7834_click'
    })
  }

  const handleOKClick = async () => {
    const normalizedFolderLevels = Array.isArray(folderLevels)
      ? folderLevels
      : [
          { level: 1, field: '' },
          { level: 2, field: undefined },
          { level: 3, field: undefined }
        ]

    const { advancedFolder, advancedNaming } = isAdvancedSelection({
      folderLevels: normalizedFolderLevels,
      namingRule
    })

    // 非会员处理
    if (!isVip) {
      if (advancedFolder || advancedNaming) {
        track('wantDownload', generateFolderNamingParamsForFooter())
        payment.openSvip('allPhotoDownloadPhoto', false, false, true)
        return
      } else {
        track('wantDownload', generateFolderNamingParamsForFooter())
        onPay()
        return
      }
    }

    // 普通会员（isVip 且非 SVip/VVIP）且包含高级功能需要检查试用次数
    if (isBasicMember && (advancedFolder || advancedNaming)) {
      const result = await refresh()
      const multi =
        result && typeof result.multiLeft !== 'undefined'
          ? result.multiLeft
          : multiLeft
      const rename =
        result && Object.prototype.hasOwnProperty.call(result, 'renameLeft')
          ? result.renameLeft
          : renameLeft
      let sourceDetail = 'webMultiCategorizedDownload'
      if (advancedFolder) {
        if (
          folderLevels[0].field === 'address' &&
          folderLevels.filter(
            (level) => level.field !== undefined && level.field !== ''
          ).length === 1
        ) {
          sourceDetail = 'webCategorizedDownload'
        }
      } else if (advancedNaming) {
        sourceDetail = 'webRenameDownload'
      }

      const multiAvailable = advancedFolder ? (multi || 0) > 0 : true
      const renameAvailable = advancedNaming
        ? typeof rename === 'undefined'
          ? true
          : (rename || 0) > 0
        : true

      if (multiAvailable && renameAvailable) {
        onOk()
        return
      }

      // 决定弹窗类型与引导：结合「是否选了该高级功能」和「次数是否用完」
      const multiExhausted = advancedFolder && (multi || 0) === 0
      const renameExhausted =
        advancedNaming &&
        (typeof rename === 'undefined' ? false : (rename || 0) === 0)

      if (multiExhausted && renameExhausted) {
        track('wantDownload', generateFolderNamingParamsForFooter())
        trackTrialLimitModal('enterPage', '全部')
        Modal.confirm({
          title: '提示',
          content:
            '正在使用「高级重命名 / 多级下载」等高级功能，试用次数已用完。你可以升级高级会员继续使用，或去掉高级功能完成下载',
          okText: '去升级',
          cancelText: '我知道了',
          onOk: () => {
            trackTrialLimitModal('去升级', '全部')
            payment.openSvip(sourceDetail, false, false, true)
          },
          onCancel: () => {
            trackTrialLimitModal('我知道了', '全部')
          }
        })
        return
      }

      if (multiExhausted) {
        track('wantDownload', generateFolderNamingParamsForFooter())
        trackTrialLimitModal('enterPage', '高级下载')
        Modal.confirm({
          icon: (
            <ExclamationCircleFilled
              style={{ color: '#faad14', fontSize: 22 }}
            />
          ),
          title: (
            <span
              style={{
                fontWeight: 500,
                fontSize: 14,
                lineHeight: 1.5,
                color: 'rgba(0, 0, 0, 0.85)'
              }}
            >
              已达到高级分类试用上限，当前配置为多级分类（2/3级文件夹），
              <span style={{ color: 'rgba(0, 112, 244, 1)' }}>
                升级会员可继续使用
              </span>
              ，或返回使用一级分类完成下载
            </span>
          ),
          okText: '升级会员',
          cancelText: '取消',
          onOk: () => {
            trackTrialLimitModal('升级会员', '高级下载')
            payment.openSvip(sourceDetail, false, false, true)
          },
          onCancel: () => {
            trackTrialLimitModal('取消', '高级下载')
          }
        })
        return
      }

      if (renameExhausted) {
        track('wantDownload', generateFolderNamingParamsForFooter())
        trackTrialLimitModal('enterPage', '照片命名')

        // 获取高级条目中文名，取前两个展示
        const advLabels = getAdvancedNamingLabels(namingRule, photoTitleList)
        const labelText =
          advLabels.length > 2
            ? `\u201C${advLabels[0]}\u201D、\u201C${advLabels[1]}\u201D等`
            : advLabels.map((l) => `\u201C${l}\u201D`).join('、')

        Modal.confirm({
          title: '提示',
          content: (
            <span>
              你选择了{labelText}高级命名方式，
              <span style={{ color: 'rgba(0, 112, 244, 1)' }}>
                请升级到高级会员使用
              </span>
              ，或去掉{labelText}高级命名继续下载。
            </span>
          ),
          okText: '升级会员',
          cancelText: '去掉高级命名',
          onOk: () => {
            trackTrialLimitModal('升级会员', '照片命名')
            payment.openSvip(sourceDetail, false, false, true)
          },
          onCancel: () => {
            const basicRule = filterBasicNamingRule(namingRule)
            trackTrialLimitModal('去掉高级命名', '照片命名', basicRule)
            if (onChangeSelectNameData) {
              onChangeSelectNameData(basicRule)
            }
            // 必须传入 basicRule：setTimeout+onOk 会拿到上一帧 handleOk 闭包里的旧 namingRule，请求仍会带高级命名
            onOk(undefined, basicRule)
          }
        })
        return
      }
    } else {
      onOk()
      return
    }
  }

  const handleBatchDownload = () => {
    if (isVip) {
      onOk('downloadOnce')
    } else {
      onDownloadOnce()
    }
  }

  useEffect(() => {
    if (isVip && isVipRef.current !== isVip) {
      onOk()
    }
    isVipRef.current = isVip
  }, [isVip])

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span>
        {isDesktop && (
          <Button onClick={handleOpenDownloadSetting}>
            <SettingOutlined />
            默认下载位置
          </Button>
        )}
      </span>
      <span>
        <Button
          id="batchDownloadBtn"
          style={{ visibility: 'hidden', width: '0px' }}
          onClick={() => {
            onOk('downloadOnce')
          }}
        >
          单次下载照片的按钮（隐藏状态）
        </Button>
        {!isVip &&
          !limitInfo?.photoViewLimit?.exist &&
          !disableBatchDownload &&
          !chargePage && ( // 命中照片查看限制或外部标记，不允许按张下载，chargePage 时不展示
            <Button
              style={{
                position: 'relative',
                width: '106px',
                marginRight: '2px'
              }}
              onClick={() => {
                handleBatchDownload()
              }}
            >
              按张下载
              <img
                src={priceIcon1}
                alt=""
                style={{
                  width: '66px',
                  height: '19px',
                  position: 'absolute',
                  right: -7,
                  top: -13
                }}
              />
            </Button>
          )}
        <Button
          type="primary"
          disabled={namingRule.length === 0}
          onClick={() => handleOKClick()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            position: 'relative'
          }}
        >
          会员免费下载
          {/* 如果选中了多级分类下载或者命名选中了高级选项或者有地点选项，则显示vip_icon */}
          <img
            style={{ width: 16, marginLeft: 8 }}
            src={shouldShowVipIcon() ? vip_icon : crown_btn_icon}
            alt=""
          />
        </Button>
      </span>
    </div>
  )
}

export default Footer
